import { SessionDetails } from '../../components/SessionDetails/SessionDetails';
import { SessionDetailView } from "../SessionDetails/types";
import { UserSessionsResult, UserSessionsResultItem, NamedPersonTable, CnfFilterType } from '../../interfaces/services';
import { REVIEW_OPTIONS, StaticFilterKeys, convertFilterFromReduxSafe, updateFilter, StaticFilter } from '../../lib/redux/store';
import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import { StaticFilterComponent } from '../../components/StaticFilter';
import { Filter, RefreshCwIcon, X, MessageSquare, Search } from 'lucide-react';
import { SessionList } from '../../components/SessionList/SessionList';
import { Typography } from '../../interfaces/typography';
import { updateForceRefresh } from '../../lib/redux/sessions/list-slice';
import { getServicesManager } from '../../services';

type HistoryProps = {
  preloadedResults?: UserSessionsResult
  
  noNavigation?: boolean,
  hideTranscriptFilter?: boolean
  hideSessionList?: boolean
  useTranscriptFilter?: boolean

  forcedSessionId?: string

  view?: SessionDetailView
  forcedFilter?: StaticFilter
  forUser?: boolean
}

export function History({
  preloadedResults,
  noNavigation,
  hideTranscriptFilter,
  hideSessionList,
  useTranscriptFilter,
  forcedSessionId,
  forcedFilter,
  forUser,
  view = SessionDetailView.MAIN,
}: HistoryProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()    
  
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [isSearching, setIsSearching] = useState(false)
  const [nameToResults, setNameToResults] = useState<{[key: string]: {displayName: string, phoneNumber: string}[]}>({})
  const [phoneToResults, setPhoneToResults] = useState<{[key: string]: {displayName: string, phoneNumber: string}[]}>({})
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null)

  const cleanNumber = (temp: string): string => temp.replace(/\D/g, "")
  const cleanName = (temp: string): string => temp.replace(/[0-9]/g, '')

  const handleSearch = async (term: string) => {
    setSearchTerm(term)
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current)

    if (!term.trim()) return
    debounceTimeout.current = setTimeout(async () => {
      setIsSearching(true)
      const cleanedNumber = cleanNumber(term)
      const cleanedName = cleanName(term).trim()

      try {
        if (cleanedNumber.length >= 10) {
          const value = await getServicesManager().getListUserSessionsV3({
            'cnf': [{'filter_type': CnfFilterType.METADATA, 'negated': false, 'metadata_filters': [{'prospect_phone_value': cleanedNumber}]}],
            'start': null,
            'end': null,
          })
            const results = (value ?? []).map(v => ({
              displayName: v.prospect_name ?? '',
              phoneNumber: v.prospect_phone_value ?? ''
            }))
            setPhoneToResults(prev => ({...prev, [cleanedNumber]: results}))
        } else if (cleanedName.length >= 2) {
          const value = await getServicesManager().getNamedPerson(cleanedName.toLowerCase()) ?? []
          const results = value
            .filter(v => v.prospect_phone_value && v.person_name && v.person_name.toLowerCase().includes(cleanedName.toLowerCase()))
            .map(v => ({
                displayName: v.person_name ?? '',
                phoneNumber: v.prospect_phone_value ?? ''
              }))
            setNameToResults(prev => ({...prev, [cleanedName]: results}))
        } 
      } finally {
        setIsSearching(false)
      }
    }, 500)
  }

  const getViewingItemSessionId = () => {
    if (forcedSessionId) return forcedSessionId
    const pageSessionId = new URLSearchParams(location.search).get('id')
    return pageSessionId ? pageSessionId : undefined
  }

  const updateRefreshCount = () => dispatch(updateForceRefresh())

  const [viewingItemSessionId, setViewingItemSessionId] = useState<string | undefined>(getViewingItemSessionId())
  const [isFilterOpen, setIsFilterOpen] = useState(getViewingItemSessionId() ? false : true)
  const [persistFilterOpen, setPersistFilterOpen] = useState(false)
  const filter = convertFilterFromReduxSafe(useSelector((state: RootState) => state.filter.value))
  const transcriptFilter = convertFilterFromReduxSafe(useSelector((state: RootState) => state.transcriptFilter.value))

  const pendingReviews = useSelector((state: RootState) => state.pendingReviews.value)

  const _renderPendingReviews = (): JSX.Element => {
    return <div onClick={() => {
      dispatch(updateFilter({...filter, [StaticFilterKeys.IS_REVIEWED]: [REVIEW_OPTIONS.REVIEW_OPENED]}))
    }} className='w-full flex flex-row items-center text-center justify-center cursor-pointer opacity-85 hover:opacity-100' style={{
      border: '0.5px solid lightpink', 'borderRadius': '5px', 'padding': '5px', 'backgroundColor': '#fcd9df'
    }}>
      <Typography variant='largeParagraph'>
        {`${pendingReviews ?? 0} pending reviews`}
      </Typography>
    </div>
  }

  const onSelectSession = (sessionId: string | undefined) => {
    if (noNavigation) setViewingItemSessionId(sessionId)
    else if (sessionId) navigate('/transcripts?id=' + sessionId)
    else navigate('/transcripts')
  }

  useEffect(() => {
    const pageSessionId = new URLSearchParams(location.search).get('id')
    if (viewingItemSessionId !== pageSessionId) setViewingItemSessionId(pageSessionId ? pageSessionId : undefined)
  }, [location])

  const sessionId = forcedSessionId ? forcedSessionId : viewingItemSessionId

  const renderSessionDetails = () => {
    if (!sessionId) return null
    return <SessionDetails 
      sessionId={sessionId} 
      view={view ?? SessionDetailView.MAIN}
      inView={true}
      onClose={hideSessionList ? undefined : () => onSelectSession(undefined)}
    />
  }

  const renderCoachingHeader = () => {
    const renderSearchResults = () => {
      if (!searchTerm.trim()) return null;

      const cleanedNumber = cleanNumber(searchTerm);
      const cleanedName = cleanName(searchTerm).trim();
      const results = [...(phoneToResults[cleanedNumber] ?? []), ...(nameToResults[cleanedName] ?? [])]

      let message = '';
      if (cleanedNumber.length > 0 && cleanedNumber.length < 10) {
        message = 'Enter a full phone number';
      } else if (cleanedName.length === 1) {
        message = 'Enter at least 2 characters';
      } else if (!isSearching && results.length === 0) {
        message = 'No matches found';
      }


      return (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-y-auto z-50">
          {isSearching ? (
            <div className="p-2 text-sm text-gray-500 flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-blue-500" />
              Searching...
            </div>
          ) : results.length > 0 ? (
            <div>
              {results.map((result, index) => (
                <div
                  key={index}
                  className="p-2 hover:bg-gray-50 cursor-pointer text-sm border-b last:border-b-0"
                  onClick={() => {
                    dispatch(updateFilter({...filter, [StaticFilterKeys.PROSPECT_PHONE_OR_NAME]: [result.phoneNumber]}));
                    setSearchTerm('');
                  }}
                >
                  {result.displayName ? `${result.displayName} (${result.phoneNumber})` : result.phoneNumber}
                </div>
              ))}
            </div>
          ) : message ? (
            <div className="p-2 text-sm text-gray-500">{message}</div>
          ) : null}
        </div>
      );
    };

    const selectedPhoneNumbers = filter[StaticFilterKeys.PROSPECT_PHONE_OR_NAME] ?? [];
    const hasSelectedFilter = selectedPhoneNumbers.length > 0;

    return (
      <div className="flex flex-col gap-2 p-3 border-b">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by phone or name..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 rounded-md border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            {renderSearchResults()}
          </div>
          <button
            onClick={() => {
              updateRefreshCount();
              dispatch(updateFilter({...filter, [StaticFilterKeys.PROSPECT_PHONE_OR_NAME]: []}));
            }}
            className="p-2 rounded-md hover:bg-gray-100 focus:outline-none text-gray-400 hover:text-gray-500"
            aria-label="Refresh"
          >
            <RefreshCwIcon className="h-5 w-5" />
          </button>
        </div>
        {hasSelectedFilter && (
          <div className="flex flex-wrap gap-2">
            {selectedPhoneNumbers.map((phoneNumber, index) => {
              // Try to find display name from our cached results
              const allResults = Object.values(phoneToResults).flat().concat(Object.values(nameToResults).flat());
              const matchingResult = allResults.find(r => r.phoneNumber === phoneNumber);
              const displayText = matchingResult?.displayName ? `${matchingResult.displayName} (${phoneNumber})` : phoneNumber;
              
              return (
                <div
                  key={index}
                  className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-md text-sm text-gray-700"
                >
                  <span>{displayText}</span>
                  <button
                    onClick={() => dispatch(updateFilter({...filter, [StaticFilterKeys.PROSPECT_PHONE_OR_NAME]: []}))}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    )
  }

  const renderMainHeader = () => {
    return (
      <div className={`flex flex-col items-center gap-1.5 border-b p-1`}>
        <div className={`flex flex-row items-center gap-2.5`}>
          <h1 className="text-2xl font-bold text-gray-900 pl-2.5">Transcripts</h1>
          <button
        onClick={(e) => {
          setPersistFilterOpen(!persistFilterOpen)
          setIsFilterOpen(!isFilterOpen)
          e.currentTarget.blur()
        }}
        className={`p-2 rounded-md focus:outline-none ${
          isFilterOpen ? 'bg-indigo-100 text-indigo-600' : 'text-gray-400 hover:text-gray-500'
        }`}
        aria-label={isFilterOpen ? "Close filters" : "Open filters"}
      >
        <Filter className="h-6 w-6" />
      </button>
      <button
        onClick={() => updateRefreshCount()}
        className="p-2 rounded-md focus:outline-none text-gray-400 hover:text-gray-500"
        aria-label="Refresh"
      >
        <RefreshCwIcon className="h-6 w-6" />
        </button>
    </div>
      {pendingReviews ? _renderPendingReviews() : null}
      </div>
    )
  }

  const renderSessionList = () => {
    return (
      <div className={`flex-1 overflow-hidden ${viewingItemSessionId && view !== SessionDetailView.COACHING ? "min-w-60 max-w-60" : "w-full"}`}>
        <SessionList
          view={view}
          forUser={forUser}
          forcedFilter={forcedFilter ? forcedFilter : useTranscriptFilter ? transcriptFilter : undefined}
          onRowClick={(item: UserSessionsResultItem) => onSelectSession(item.session_id)}
          shownSessionId={viewingItemSessionId}
        />
      </div>
    )
  }

  const renderSessionListSection = () => {
    return (
    <div className={`flex flex-col h-full pl-1 ${!sessionId || view === SessionDetailView.COACHING ? 'flex-grow w-full' : ''}`}>
        {view === SessionDetailView.MAIN && renderMainHeader()}
        {renderSessionList()}
      </div>
    )
  }

  const renderDefaultSessionDetailsSection = () => {
    return (
      <div className='flex-1 pl-0.5 h-full flex flex-col gap-2'>
        {renderSessionDetails()} 
      </div>
    )
  }

  const renderCoachingSessionDetailsSection = () => {
    return (
      <div 
        className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onSelectSession(undefined)
          }
        }}
      >
        <div 
          className="bg-white rounded-lg h-4/5 overflow-hidden flex flex-col"
          style={{
            width: '90%'
          }}
        >
          {renderSessionDetails()}
        </div>
      </div>
    );
  }

  const renderSessionDetailsSection = () => {
    switch (view) {
      case SessionDetailView.COACHING:
        return renderCoachingSessionDetailsSection();
      default:
        return renderDefaultSessionDetailsSection();
    }
  }

  const renderDefaultFilter = () => {
    return (
    <StaticFilterComponent
          applicableSpecialFilters={[StaticFilterKeys.PROSPECT_PHONE_OR_NAME, StaticFilterKeys.IS_REVIEWED, StaticFilterKeys.IS_STARRED]}
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
        />
    )
  }

  const renderStaticFilter = () => {
    switch (view) {
      default:
        return renderDefaultFilter()
    }
  }

  return (
    <div className="h-full min-w-fit w-full mx-auto flex flex-row overflow-x-auto pb-1.5">
      {!hideTranscriptFilter && renderStaticFilter()}
      <div className={`flex flex-col h-full pl-1 ${!sessionId || view === SessionDetailView.COACHING ? 'flex-grow w-full' : ''}`}>
        {view === SessionDetailView.COACHING ? renderCoachingHeader() : view === SessionDetailView.MAIN && renderMainHeader()}
        {renderSessionList()}
      </div>
      {sessionId !== undefined && renderSessionDetailsSection()}
    </div>
  )
}
  